import { createFileRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { Button } from '@mantine/core';
import {
  IconCalendarWeek, IconClipboardList, IconLayoutDashboard, IconLayoutKanban,
  IconPlus, IconTable, IconTargetArrow, IconTimeline,
} from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { MaeShell } from '@/shared/desktop/MaeShell';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { MobileTasks } from '@/features/tasks/mobile/MobileTasks';
import { MOBILE_SECTION_PATHS } from '@/features/tasks/mobile/mobileViews';
import { TaskSectionShell } from '@/features/tasks/TaskSectionShell';
import { NEW_TASK } from '@/features/tasks/sectionContext';
import { useHasPeopleUnder } from '@/features/tasks/dashboard/useHasPeopleUnder';
import { TASK_LIST_KEYS } from '@/features/tasks/taskKeys';
import { TASK_NO_CREATE_REASON, useTaskPermissions } from '@/features/tasks/taskPermissions';
import { MaeGate } from '@/shared/components/MaeGate';
import type { SectionTab } from '@/features/tasks/sectionContext';

export const Route = createFileRoute('/_app/tasks')({
  /* Il task aperto sta nell'URL: cosi' F5 lo ritrova e un link lo porta. */
  validateSearch: (s: Record<string, unknown>): { task?: string } => ({
    task: typeof s.task === 'string' && s.task ? s.task : undefined,
  }),
  component: TasksPage,
});

/**
 * Chi rende la sezione: la schermata mobile o il layout desktop a schede.
 *
 * Su telefono le **cinque viste mobili** stanno tutte dentro `MobileTasks`, che porta testata,
 * numeri e selettore. Prima solo `/tasks` era mobile e le altre quattro rendevano il componente
 * desktop nudo — senza barra, senza Indietro e senza modo di tornare alle altre viste: un vicolo
 * cieco raggiungibile solo per URL. Le rotte fuori dalla sezione (`/tasks/new`, `/tasks/$taskId`)
 * restano schermate a sé, con la loro testata.
 *
 * ⚠️ Su scrivania le viste sono **sette**: «Scheda» e «Cruscotto» esistono solo qui, e le loro rotte
 * mandano altrove chi ci arriva da un telefono. Il perché in `tasks.card.tsx` e `tasks.dashboard.tsx`.
 */
function TasksPage() {
  const isMobile = useIsMobileShell();
  const path = useRouterState({ select: (s) => s.location.pathname });
  if (isMobile) {
    return MOBILE_SECTION_PATHS.has(path)
      ? <MobileTasks><Outlet /></MobileTasks>
      : <Outlet />;
  }
  return <TasksLayout />;
}

/**
 * Le sette viste della sezione, su scrivania. `/tasks/gantt-interactive` rinvia a `/tasks/gantt`.
 */
const DETAILS: readonly SectionTab[] = [
  /*
   * L'agenda è la **prima scheda e il punto d'arrivo** (§10.2): `/tasks` apre lei, ed è la
   * lettura con cui si comincia la giornata — elenco e bacheca sono dove si va a spostare le
   * cose. Deciso l'08-09-2026.
   *
   * ⚠️ Da qui l'elenco ha un indirizzo suo, `/tasks/list`: prima era `/tasks`. Un vecchio
   * segnalibro su `/tasks` non si rompe — apre l'agenda — ma non porta più dove portava. Le
   * notifiche e i collegamenti diretti a un task non c'entrano: viaggiano su `/tasks?task=<id>`,
   * che è un parametro della **sezione** e vale qualunque scheda sia aperta.
   */
  { value: 'agenda', label: 'Agenda', path: '/tasks', icon: <IconCalendarWeek size={16} /> },
  { value: 'list', label: 'Lista', path: '/tasks/list', icon: <IconTable size={16} /> },
  { value: 'kanban', label: 'Kanban', path: '/tasks/kanban', icon: <IconLayoutKanban size={16} /> },
  { value: 'gantt', label: 'Gantt', path: '/tasks/gantt', icon: <IconTimeline size={16} /> },
  { value: 'plan', label: 'Piano', path: '/tasks/plan', icon: <IconTargetArrow size={16} /> },
  /*
   * «Scheda» è **in coda e non in mezzo**, e non è una scelta di importanza: i pulsanti del
   * selettore non si devono spostare. Sono bersagli che si imparano a memoria — il commento di
   * `SelettoreVista` racconta la volta in cui, cambiando vista, si andava a cliccare dove c'era
   * Gantt e si prendeva Piano. Infilarla fra Lista e Kanban avrebbe spostato le quattro dopo.
   *
   * È l'unica vista che porta `dettaglioInPrimoPiano`: la coda si stringe a guida e la scheda
   * del task prende la pagina. Vedi `CardView` e `TaskSectionShell`.
   */
  { value: 'card', label: 'Scheda', path: '/tasks/card', icon: <IconClipboardList size={16} /> },
  /*
   * «Cruscotto» è la vista del capogruppo: le persone di cui è responsabile con ore pianificate
   * contro capacità, i loro task aperti, le commesse su cui lavorano con le date di fine, le
   * scadenze. Compone quel che l'Agenda («Il gruppo») e il Piano dicono a pezzi. In coda per la
   * stessa ragione di «Scheda»; solo da scrivania, vedi `tasks.dashboard.tsx`.
   *
   * Ha assorbito «Capacità», tolta il 12-10-2026: `/tasks/capacity` rimanda qui.
   */
  { value: 'dashboard', label: 'Cruscotto', path: '/tasks/dashboard', icon: <IconLayoutDashboard size={16} /> },
];

/**
 * La pagina della sezione Task.
 *
 * Non compone più niente da sé: intestazione, striscia dei numeri e schede stanno nella
 * cornice condivisa (`TaskSectionShell`), che è ciò che rende le sette viste **la stessa
 * pagina**. Prima le tessere dei conteggi comparivano solo sulla Lista — come se i totali
 * dell'azienda dipendessero da come li stai guardando. Gli Appunti sono una pagina a sé, `/notes`.
 */
function TasksLayout() {
  const navigate = useNavigate();
  const perms = useTaskPermissions();
  const path = useRouterState({ select: (s) => s.location.pathname });
  // Match più specifico (path più lungo) fra le schede visibili.
  const vista = [...DETAILS]
    .filter((t) => (t.path === '/tasks' ? path === '/tasks' : path.startsWith(t.path)))
    .sort((a, b) => b.path.length - a.path.length)[0];

  /*
   * IL CLIC SULLA SCHEDA RISPONDE SUBITO, ANCHE SE LA VISTA ARRIVA DOPO.
   *
   * TanStack Router avvolge le navigazioni in `React.startTransition`: React tiene a schermo la
   * **vecchia** vista finche' la nuova non e' pronta, e `location` — da cui si ricava quale
   * scheda e' attiva — cambia dentro quella transizione. Con viste che al primo render
   * costruiscono colonne o barre da duecento righe, il risultato era un clic che per qualche
   * decimo di secondo non produceva **niente**: ne' la scheda si accendeva, ne' il contenuto
   * cambiava. Un'interfaccia che non risponde si legge come lenta, anche mentre sta lavorando.
   *
   * Qui la scheda scelta si segna in uno stato locale — aggiornato in modo urgente, fuori dalla
   * transizione — e si spegne quando il percorso arriva davvero. Nel frattempo la cornice mette
   * il caricamento al posto della vista vecchia: il tempo e' lo stesso, ma il gesto ha un esito.
   */
  const [incoming, setIncoming] = useState<SectionTab['value'] | null>(null);
  useEffect(() => { setIncoming(null); }, [path]);
  const active = incoming ?? vista?.value ?? 'list';
  /* Il Cruscotto è di chi ha persone sotto di sé; chi ci è già resta. */
  const peopleUnder = useHasPeopleUnder();
  const details = peopleUnder.has === true || active === 'dashboard'
    ? DETAILS
    : DETAILS.filter((t) => t.value !== 'dashboard');

  /*
   * `/tasks/<qualcosa-che-non-è-una-vista>` è l'id di un task (o `new`), e su desktop il task si
   * apre nel pannello: si va a `/tasks?task=<id>`.
   *
   * ⚠️ Il redirect nasceva **qui** perché `tasks.$taskId.tsx` su desktop non veniva montato:
   * senza una vista corrispondente `attiva` ricadeva su `list` e questo layout rendeva l'elenco
   * invece dell'`Outlet`. **Da quando `/tasks` è l'Agenda quel ramo rende l'`Outlet`**, quindi
   * ora girano entrambi — e vanno alla stessa destinazione con `replace`, che è il caso per cui
   * quel `<Navigate>` era stato lasciato là. Restano due perché togliere questo lascerebbe il
   * redirect al solo componente mobile-first, e togliere quello rimetterebbe un `<Navigate>` che
   * non si sa se gira.
   */
  const idDalPath = !vista && path.startsWith('/tasks/') ? path.slice(7).split('/')[0] : '';
  useEffect(() => {
    if (idDalPath) void navigate({ to: '/tasks', search: { task: idDalPath }, replace: true });
  }, [idDalPath, navigate]);

  const { task: openTask } = Route.useSearch();

  /*
   * `replace` se un task era già aperto: cambiarlo non deve impilare voci nella cronologia.
   *
   * `ignoraGuardia` serve a un caso solo, ma necessario: appena un task è **nato**, il pannello
   * passa a mostrarlo, e senza questo il guardiano del form sporco blocca quella navigazione —
   * `form.reset()` è già avvenuto, ma il blocker è agganciato con la closure del render prima e
   * `isDirty` là è ancora vero. Misurato: il task veniva creato e poi si chiedeva se si volevano
   * perdere le modifiche appena salvate.
   */
  const apriTask = useCallback((id: string | null, opz?: { ignoreGuard?: boolean }) => {
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, task: id ?? undefined }),
      replace: Boolean(openTask),
      ignoreBlocker: opz?.ignoreGuard,
    });
  }, [navigate, openTask]);

  return (
    /* `altezzaPiena`: la pagina sta nello schermo e lo scorrimento vive **dentro** — la
       superficie dell'elenco e la scheda del task. Prima scorreva tutto insieme, e su un elenco
       lungo l'intestazione con i filtri attivi usciva dallo schermo: si scorreva senza sapere
       piu' su cosa. Vale per la sezione Task perche' le sue viste sono scritte per
       starci; una pagina che non lo e' va tagliata, e allora e' meglio una barra. */
    <MaeShell fullHeight>
      <TaskSectionShell
        details={details}
        active={active}
        /* `search: (prev) => prev` o cambiare vista perderebbe il task aperto. */
        onChangeDetail={(p) => {
          /* Sulla scheda gia' aperta non si anticipa niente: il percorso non cambierebbe, quindi
             l'effetto qui sopra non scatterebbe e il caricamento resterebbe li' per sempre. */
          if (p === path) return;
          setIncoming(DETAILS.find((t) => t.path === p)?.value ?? null);
          /* `finally` e non `then`: se il guardiano del form blocca il passaggio e si risponde
             «resta qui», il percorso non cambia mai — e senza questo la vista resterebbe coperta
             dal caricamento di un cambio che non e' avvenuto. */
          void navigate({ to: p, search: (prev) => prev }).finally(() => setIncoming(null));
        }}
        /* «Nuovo task» è una navigazione come tutto il resto: `?task=new`. La cornice decide se
           mostrarlo nel pannello o, a finestra stretta, nella modale. */
        defaultAction={() => apriTask(NEW_TASK)}
        /* Il task aperto vive nell'URL e la cornice lo riceve: resta aperto fra le viste, e in
           piu' sopravvive a un F5 ed e' indirizzabile. */
        openTask={openTask ?? null}
        onOpenTask={apriTask}
        changeInProgress={incoming !== null}
        /* Sulla vista «Scheda» il dettaglio non sta accanto alla vista: **è** la vista, e la coda
           si stringe a guida. Lo dice la rotta perché è qui che sta l'elenco delle viste, e la
           cornice deve saperlo al primo render. Vedi `TaskSectionShell`. */
        foregroundDetail={active === 'card'}
        /* Tutte le viste che leggono task, da `taskKeys.ts`: scritto a mano qui, l'elenco
           dimenticava il piano e l'elenco desktop — e conteneva `['tasks']`, che nessuno legge. */
        invalidateKeys={TASK_LIST_KEYS as string[][]}
        action={(run) => (
          <MaeGate when={perms.create} mode="disable" reason={TASK_NO_CREATE_REASON}>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={run}
              disabled={!perms.create}
              style={{ height: 38, borderRadius: 12 }}
            >
              Nuovo task
            </Button>
          </MaeGate>
        )}
      >
        {/* Tutte le viste passano dall'`Outlet`, Agenda compresa: la sua rotta indice è
            `tasks.index.tsx`. Renderla qui a mano la lasciava fuori dal guscio mobile, che di
            questo layout non passa. */}
        <Outlet />
      </TaskSectionShell>

    </MaeShell>
  );
}
